import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  Task,
  TaskWithSubtasks,
  TaskStatusValue,
  DelayReasonCodeValue,
  MyTasksResponse,
  ProjectWithProgress,
} from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';
import { myTasksKeys } from './use-my-tasks';
import { projectRecentUpdatesKeys } from './use-project-recent-updates';
import { patchProjectProgressCache } from './use-project-detail';

// Keep client-side "fetch all pages" requests within the shared API pagination cap.
const TASK_LIST_PAGE_SIZE = 100;

// ── Query keys ───────────────────────────────────────────────────────────────

export const taskKeys = {
  list: (projectId: string, filters?: Record<string, unknown>) =>
    ['tasks', projectId, filters] as const,
  detail: (taskId: string) => ['task', taskId] as const,
};

function invalidateProjectRecentUpdates(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  void queryClient.invalidateQueries({ queryKey: projectRecentUpdatesKeys.list(projectId) });
}

/** Drop the two cache entries that carry `progress_pct` and the project status
 *  badge. Every task mutation changes at least one of them, so this belongs on
 *  all of them.
 *
 *  Both invalidations are required: `['projects']` does NOT prefix-match
 *  `['project', id]` — TanStack matches keys positionally, and these two share
 *  no common prefix. Missing them is why a completed task left the progress bar
 *  and the "Active" badge stale; with staleTime and cache persistence on, the
 *  wrong percentage could even survive a reload.
 *
 *  `['projects', 'recent']` (the home strip) is covered by the `['projects']`
 *  prefix. */
export function invalidateProjectProgress(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
  void queryClient.invalidateQueries({ queryKey: ['projects'] });
}

/** Every cached task detail and Dependency panel in this project. A change to
 *  one task can move another task's Waiting on / Needed by count, and nothing
 *  on hand says which tasks are linked to it (a project_change_events row for a
 *  task_dependency names the link, not a task). Scoped to the project so an
 *  open task elsewhere isn't refetched; `['task', id, 'dependencies']` carries
 *  no project, so it is matched through its task's cached detail, and a key
 *  whose detail isn't cached is included rather than guessed at. */
export function invalidateProjectTaskViews(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  return Promise.all([
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        if (queryKey[0] !== 'task' || typeof queryKey[1] !== 'string') return false;
        const taskProjectId = queryClient.getQueryData<{
          data?: { task?: { project_id?: string } };
        }>(taskKeys.detail(queryKey[1]))?.data?.task?.project_id;
        return taskProjectId === undefined || taskProjectId === projectId;
      },
    }),
    queryClient.invalidateQueries({ queryKey: ['task-dependency-candidates'] }),
  ]);
}

type TaskListFilters = {
  status?: string;
  priority?: string;
  parent_only?: boolean;
  page?: number;
  pageSize?: number;
};

type TaskListResponse = {
  data?: {
    tasks: Task[];
    pagination: { page: number; pageSize: number; total: number };
  };
};

async function fetchTaskListPage(projectId: string, filters?: TaskListFilters) {
  const queryString = new URLSearchParams(
    Object.entries(filters ?? {})
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  ).toString();

  return apiClient<{
    tasks: Task[];
    pagination: { page: number; pageSize: number; total: number };
  }>(`/api/v1/projects/${projectId}/tasks${queryString ? `?${queryString}` : ''}`);
}

// ── Task list ────────────────────────────────────────────────────────────────

export function useTaskList(projectId: string, filters?: TaskListFilters) {
  return useQuery({
    queryKey: taskKeys.list(projectId, filters),
    queryFn: () => fetchTaskListPage(projectId, filters),
    enabled: !!projectId,
  });
}

export function useAllTaskList(
  projectId: string,
  filters?: Omit<TaskListFilters, 'page' | 'pageSize'>,
) {
  return useQuery({
    queryKey: taskKeys.list(projectId, { ...filters, all: true }),
    queryFn: async (): Promise<TaskListResponse> => {
      let page = 1;
      let total = 0;
      const tasks: Task[] = [];

      while (true) {
        const result = await fetchTaskListPage(projectId, {
          ...filters,
          page,
          pageSize: TASK_LIST_PAGE_SIZE,
        });
        const nextTasks = result.data?.tasks ?? [];
        const pagination = result.data?.pagination;

        tasks.push(...nextTasks);
        total = pagination?.total ?? tasks.length;

        if (!pagination || nextTasks.length === 0 || tasks.length >= total) {
          break;
        }

        page += 1;
      }

      return {
        data: {
          tasks,
          pagination: {
            page: 1,
            pageSize: tasks.length || TASK_LIST_PAGE_SIZE,
            total,
          },
        },
      };
    },
    enabled: !!projectId,
  });
}

// ── Task detail ──────────────────────────────────────────────────────────────

export function useTaskDetail(taskId: string | null) {
  return useQuery({
    queryKey: taskKeys.detail(taskId ?? ''),
    queryFn: () => apiClient<{ task: TaskWithSubtasks }>(`/api/v1/tasks/${taskId}`),
    enabled: !!taskId,
  });
}

// ── Create task ──────────────────────────────────────────────────────────────

export function useCreateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      description?: string;
      priority?: string;
      assigned_user_id?: string;
      assigned_user_ids?: string[];
      deadline?: string;
      start_date?: string;
      parent_task_id?: string;
    }) =>
      apiClient<{ task: Task }>(`/api/v1/projects/${projectId}/tasks`, {
        method: 'POST',
        // Manual create → top of the list, so you land on what you just typed.
        // The template-apply loop POSTs directly and keeps the server's
        // 'bottom' default, which is what preserves the template's order.
        body: JSON.stringify({ ...body, placement: 'top' }),
      }),
    onSuccess: () => {
      toast.success('Task created');
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
      invalidateProjectProgress(queryClient, projectId);
    },
  });
}

// ── Create subtask ───────────────────────────────────────────────────────────

export function useCreateSubtask(parentTaskId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      description?: string;
      priority?: string;
      assigned_user_id?: string;
      assigned_user_ids?: string[];
      start_date?: string;
      deadline?: string;
    }) =>
      apiClient<{ task: Task }>(`/api/v1/tasks/${parentTaskId}/subtasks`, {
        method: 'POST',
        body: JSON.stringify({ ...body, placement: 'top' }),
      }),
    onSuccess: () => {
      toast.success('Subtask created');
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(parentTaskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
      invalidateProjectProgress(queryClient, projectId);
    },
  });
}

// ── Update task ──────────────────────────────────────────────────────────────

export function useUpdateTask(taskId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title?: string;
      description?: string | null;
      priority?: string;
      assigned_user_id?: string | null;
      assigned_user_ids?: string[] | null;
      deadline?: string | null;
      start_date?: string | null;
      version: number;
    }) =>
      apiClient<{ task: Task }>(`/api/v1/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast.success('Task updated');
      void queryClient.invalidateQueries({ queryKey: ['task'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
      invalidateProjectProgress(queryClient, projectId);
    },
  });
}

// ── Delete task ──────────────────────────────────────────────────────────────

export function useDeleteTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => apiClient(`/api/v1/tasks/${taskId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Task deleted');
      // The delete cascades its Dependency links away with no audit row of
      // their own, so surviving linked tasks only refresh through this.
      void invalidateProjectTaskViews(queryClient, projectId);
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      void queryClient.invalidateQueries({ queryKey: myTasksKeys.list() });
      invalidateProjectRecentUpdates(queryClient, projectId);
      invalidateProjectProgress(queryClient, projectId);
    },
  });
}

// ── Change task status ───────────────────────────────────────────────────────

type ChangeStatusBody = {
  status: string;
  delay_reason_code?: string;
  delay_reason_text?: string;
  version: number;
};

type ChangeStatusContext = {
  prevDetail?: { data: { task: TaskWithSubtasks } };
  prevMyTasks?: { data: MyTasksResponse };
};

// The status endpoint returns the project's freshly-synced progress alongside
// the task, so the bar and badge can repaint without a follow-up GET. Absent
// when the project row has gone away.
type TaskMutationResponse = {
  data: {
    task: Task;
    project?: {
      progress_pct: number;
      status: string;
      task_count: number;
      completed_task_count: number;
    };
  };
};

export function useChangeTaskStatus(taskId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<TaskMutationResponse, Error, ChangeStatusBody, ChangeStatusContext>({
    mutationFn: (body) =>
      apiClient<{ task: Task }>(`/api/v1/tasks/${taskId}/status`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Optimistic update: flip the badge immediately on both the detail and
    // every cached list view, roll back in onError. Keeps the primary
    // interaction feeling instant even on slow networks.
    onMutate: async (body) => {
      const detailKey = ['task', taskId];
      await queryClient.cancelQueries({ queryKey: detailKey });
      await queryClient.cancelQueries({ queryKey: ['tasks', projectId] });

      // Task.status is typed as the general-workflow enum but tasks.status in
      // the DB accepts any workflow's keys (see migration 20260418000001).
      // The cast is safe at runtime; the type annotation is historical.
      const nextStatus = body.status as TaskStatusValue;
      const nextReasonCode = (body.delay_reason_code ?? null) as DelayReasonCodeValue | null;

      const prevDetail = queryClient.getQueryData<{ data: { task: TaskWithSubtasks } }>(detailKey);
      const prevMyTasks = queryClient.getQueryData<{ data: MyTasksResponse }>(myTasksKeys.list());
      if (prevDetail?.data?.task) {
        queryClient.setQueryData(detailKey, {
          data: {
            task: {
              ...prevDetail.data.task,
              status: nextStatus,
              delay_reason_code: nextReasonCode,
              delay_reason_text: body.delay_reason_text ?? null,
              version: prevDetail.data.task.version + 1,
            },
          },
        });
      }

      queryClient.setQueriesData<{ data: { tasks: Task[] } }>(
        { queryKey: ['tasks', projectId] },
        (old) => {
          if (!old?.data?.tasks) return old;
          return {
            ...old,
            data: {
              ...old.data,
              tasks: old.data.tasks.map((t) =>
                t.id === taskId
                  ? {
                      ...t,
                      status: nextStatus,
                      delay_reason_code: nextReasonCode,
                      delay_reason_text: body.delay_reason_text ?? null,
                      version: t.version + 1,
                    }
                  : t,
              ),
            },
          };
        },
      );

      if (prevMyTasks?.data?.rows) {
        queryClient.setQueryData(myTasksKeys.list(), {
          data: {
            ...prevMyTasks.data,
            rows: prevMyTasks.data.rows.map((row) =>
              row.id === taskId
                ? {
                    ...row,
                    status: nextStatus,
                    delay_reason_code: nextReasonCode,
                    delay_reason_text: body.delay_reason_text ?? null,
                    version: row.version + 1,
                    updated_at: new Date().toISOString(),
                  }
                : row,
            ),
          },
        });
      }

      return { prevDetail, prevMyTasks };
    },
    onError: (err, _body, ctx) => {
      if (ctx?.prevDetail) {
        queryClient.setQueryData(['task', taskId], ctx.prevDetail);
      }
      if (ctx?.prevMyTasks) {
        queryClient.setQueryData(myTasksKeys.list(), ctx.prevMyTasks);
      }
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      toast.error(err instanceof Error ? err.message : 'Status change failed');
    },
    onSuccess: () => {
      toast.success('Status updated');
    },
    onSettled: (result) => {
      // Covers this task, its parent and every task waiting on it or needed by it.
      void invalidateProjectTaskViews(queryClient, projectId);
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      void queryClient.invalidateQueries({ queryKey: myTasksKeys.list() });
      invalidateProjectRecentUpdates(queryClient, projectId);

      // Paint the server's fresh numbers straight into the detail cache so the
      // bar and badge move on this tick, then invalidate so everything else
      // (the list, the home strip) catches up on its own schedule.
      const project = result?.data?.project;
      if (project) {
        patchProjectProgressCache(queryClient, projectId, {
          // The server derives this from the same ProjectStatusValue union the
          // type names, but it crosses the wire as a plain string. The cast is
          // safe at runtime; there is no narrower type to receive it as.
          status: project.status as ProjectWithProgress['status'],
          progress_pct: project.progress_pct,
          task_count: project.task_count,
          completed_task_count: project.completed_task_count,
        });
      }
      invalidateProjectProgress(queryClient, projectId);
    },
  });
}

// ── Assign task ──────────────────────────────────────────────────────────────

export function useAssignTask(taskId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignedUserId: string) =>
      apiClient<{ task: Task }>(`/api/v1/tasks/${taskId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assigned_user_id: assignedUserId }),
      }),
    onSuccess: () => {
      toast.success('Task assigned');
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
    },
  });
}

export function useManageTaskAssignees(taskId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { assignedUserIds: string[]; projectMemberIdsToAdd: string[] }) => {
      const assignedUserIds = uniqueIds(body.assignedUserIds);
      const projectMemberIdsToAdd = uniqueIds(body.projectMemberIdsToAdd);

      for (const userId of projectMemberIdsToAdd) {
        await apiClient(`/api/v1/projects/${projectId}/members`, {
          method: 'POST',
          body: JSON.stringify({ user_id: userId }),
        });
      }

      return apiClient<{ task: Task }>(`/api/v1/tasks/${taskId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assigned_user_ids: assignedUserIds }),
      });
    },
    onSuccess: () => {
      toast.success('Assignees updated');
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['project-users', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['project-non-members', projectId] });
      void queryClient.invalidateQueries({ queryKey: myTasksKeys.list() });
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      invalidateProjectRecentUpdates(queryClient, projectId);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update assignees');
    },
  });
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}
